import { AssetClassCode, Condition, Period } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ConditionSection } from '~/components/condition-section/ConditionSection';
import { PriceMovement } from '~/components/price-movement/PriceMovement';
import { useEntity } from '~/hooks/use-entities';
import { ScenarioWidgetData } from '~/stores/use-chat/use-chat.types';
import { generateConditionString } from '~/utils/condition-utils/condition-utils';
import {
  getEpisodeByPeriod,
  getPastEpisodesCount,
  getTradingDaysCount,
  RELATIVE_IDX_MIN,
} from '~/utils/domino-utils/domino-utils';

import { InsightsReaction } from '../insight-widget/components/insights-drawer/components/insights-reaction/InisghtsReaction';
import * as S from './ScenarioWidget.styles';

const DEFAULT_PERIOD: Period = '6m';

type ScenarioWidgetProps = Omit<ScenarioWidgetData, 'confidence'>;

export const ScenarioWidget = ({
  entityTag,
  episodes,
  prediction,
  conditions,
  period = DEFAULT_PERIOD,
}: ScenarioWidgetProps) => {
  const { data = [] } = useEntity(entityTag);
  const [entity] = data;

  const { t } = useTranslation(['scenarioCommander', 'analyze']);

  const currentEpisode = getEpisodeByPeriod(episodes, period);
  const pastEpisodesCount = getPastEpisodesCount(currentEpisode);
  const tradingDaysCount = getTradingDaysCount(currentEpisode);
  const isFixedIncome = entity?.asset_class === AssetClassCode.Fi;

  const condition = generateConditionString({
    t,
    conditions: conditions as Condition[],
  });

  return (
    <S.ScenarioWidgetRoot data-testid="scenario-widget">
      <ConditionSection
        condition={condition}
        episodesCount={pastEpisodesCount}
        totalTradingDays={tradingDaysCount}
      />

      {pastEpisodesCount > 0 && (
        <InsightsReaction
          entityName={entity?.name || entityTag}
          episodeCount={pastEpisodesCount}
          isFixedIncome={isFixedIncome}
        />
      )}

      <PriceMovement
        prediction={prediction}
        episodes={episodes}
        horizonToIndexOffset={-RELATIVE_IDX_MIN + 1}
        isFixedIncome={isFixedIncome}
        useDynamicDaysLabel
      />
    </S.ScenarioWidgetRoot>
  );
};
